import { Vec3, vec3, add, sub, scale, dot, length, normalize, distance } from '../core/math/vec3'
import { Quat, QUAT_IDENTITY, rotate, fromAxisAngle } from '../core/math/quat'
import { AttachNode, PartDef, Shape } from '../core/vessel/part'
import { Vessel, PartInstance } from '../core/vessel/vessel'
import { PartCatalog, partDef } from '../core/vessel/catalog'
import { shapeHalfHeight, shapeRadius } from '../core/vessel/extents'

/** Tolleranza con cui due nodi si considerano nello stesso punto. */
const EPS = 1e-4

export interface NodoLibero {
  readonly partId: string
  readonly nodeId: string
  /** posizione nel sistema del veicolo */
  readonly position: Vec3
  /** direzione verso l'esterno, nel sistema del veicolo */
  readonly direction: Vec3
  readonly size: number
  readonly kind: 'axial' | 'radial'
}

/** I nodi di una parte gia montata, portati in coordinate del veicolo. */
export function nodiDiParte(part: PartInstance, def: PartDef): NodoLibero[] {
  return def.nodes.map((n) => ({
    partId: part.id,
    nodeId: n.id,
    position: add(part.position, rotate(part.rotation, n.position)),
    direction: rotate(part.rotation, n.direction),
    size: n.size,
    kind: n.kind,
  }))
}

/**
 * I nodi non ancora occupati.
 *
 * L'occupazione non si tiene in un registro ma si legge dalla geometria: un
 * nodo e occupato se un'altra parte ha un proprio nodo nello stesso punto.
 * E piu robusto di un registro da aggiornare a ogni operazione, e non
 * costringe a cambiare i tipi di core/.
 */
export function nodiLiberi(vessel: Vessel, catalog: PartCatalog): NodoLibero[] {
  const tutti = vessel.parts.flatMap((p) => nodiDiParte(p, partDef(catalog, p.defId)))
  return tutti.filter((n) =>
    !tutti.some((altro) => altro.partId !== n.partId && distance(altro.position, n.position) < EPS))
}

/** Due nodi si agganciano se guardano l'uno verso l'altro e hanno la stessa taglia. */
export function compatibili(libero: NodoLibero, nodo: AttachNode): boolean {
  if (libero.kind !== nodo.kind) return false
  if (libero.kind === 'axial') {
    if (Math.abs(libero.size - nodo.size) > 1e-6) return false
    // senza rotazione della parte nuova, i versi devono essere opposti
    return dot(libero.direction, nodo.direction) < -0.99
  }
  return true
}

export interface Aggancio {
  readonly parentId: string
  readonly parentNodeId: string
  readonly ownNodeId: string
  readonly position: Vec3
  readonly rotation: Quat
}

/** Tutti i modi di agganciare una parte nuova al veicolo, per via assiale. */
export function agganciPossibili(vessel: Vessel, catalog: PartCatalog, defId: string): Aggancio[] {
  if (vessel.parts.length === 0) return []
  const def = partDef(catalog, defId)
  const out: Aggancio[] = []

  for (const libero of nodiLiberi(vessel, catalog)) {
    if (libero.kind !== 'axial') continue
    for (const nodo of def.nodes) {
      if (!compatibili(libero, nodo)) continue
      out.push({
        parentId: libero.partId,
        parentNodeId: libero.nodeId,
        ownNodeId: nodo.id,
        // la parte nuova non ruota: basta traslarla perche i nodi coincidano
        position: sub(libero.position, nodo.position),
        rotation: QUAT_IDENTITY,
      })
    }
  }
  return out
}

/** L'aggancio piu vicino a un punto, entro un raggio. Null se non c'e. */
export function agganciaVicino(
  vessel: Vessel, catalog: PartCatalog, defId: string, punto: Vec3, raggio: number,
): Aggancio | null {
  let migliore: Aggancio | null = null
  let minima = raggio

  for (const a of agganciPossibili(vessel, catalog, defId)) {
    const nodo = nodiLiberi(vessel, catalog)
      .find((n) => n.partId === a.parentId && n.nodeId === a.parentNodeId)!
    const d = distance(nodo.position, punto)
    if (d < minima) { minima = d; migliore = a }
  }
  return migliore
}

/** Raggio del fianco di una parte: dove si appoggia un aggancio radiale. */
export function raggioFianco(shape: Shape): number {
  return shapeRadius(shape)
}

/**
 * Aggancio radiale al fianco di una parte, a una data quota e a un dato
 * azimut. La parte nuova viene ruotata attorno all'asse del razzo cosi che il
 * suo nodo radiale guardi verso l'interno: e questo che fa uscire la pala di
 * un'aletta verso l'esterno invece che di traverso.
 */
export function agganciaRadiale(
  vessel: Vessel, catalog: PartCatalog, defId: string,
  targetId: string, quotaLocale: number, azimut: number,
): Aggancio | null {
  const def = partDef(catalog, defId)
  const nodo = def.nodes.find((n) => n.kind === 'radial')
  if (!nodo) return null

  const target = vessel.parts.find((p) => p.id === targetId)
  if (!target) return null
  const defTarget = partDef(catalog, target.defId)

  const semiAltezza = shapeHalfHeight(defTarget.shape)
  const y = target.position.y + Math.max(-semiAltezza, Math.min(semiAltezza, quotaLocale))
  const radiale = vec3(Math.cos(azimut), 0, Math.sin(azimut))

  // il nodo della parte guarda verso −Z locale: ruotando di π/2 − azimut
  // attorno a Y, quel verso diventa −radiale, cioe verso l'asse del razzo
  const rotazione = fromAxisAngle(vec3(0, 1, 0), Math.PI / 2 - azimut)
  const superficie = add(
    vec3(target.position.x, y, target.position.z),
    scale(radiale, raggioFianco(defTarget.shape)),
  )

  return {
    parentId: targetId,
    parentNodeId: 'fianco',
    ownNodeId: nodo.id,
    position: sub(superficie, rotate(rotazione, nodo.position)),
    rotation: rotazione,
  }
}
